import React from "react";

/**
 * SmartMedia — large rectangular media surface for product/proof cards.
 *
 * Behavior (per spec):
 *   • Renders <img> or <video> in a rectangular slot (NEVER circle-cropped).
 *   • Auto-detects portrait vs landscape on load:
 *       - portrait / square     → object-fit: contain (with elegant dark padding)
 *       - landscape             → object-fit: cover  (fills cleanly)
 *   • Optional explicit `fit="contain"|"cover"` override.
 *   • Optional play-button overlay for video URLs (auto-detected).
 *   • Aspect ratio defaults to 4/3 (taller media area for premium feel) but is
 *     overridable via `aspect`.
 */
export function SmartMedia({
  src,
  poster,
  alt = "",
  className = "",
  aspect = "4/3",
  rounded = "rounded-xl",
  bg = "bg-[#0a0c08]",
  fit = "auto",                   // "auto" | "cover" | "contain"
  showPlay = true,
  badge = null,                   // top-left ribbon ("MEMBERS DROP", etc.)
  videoBadge = true,              // top-right "VIDEO" pill on video media
  onClick,
  children,
}) {
  const isVideo = isVideoUrl(src);
  // For videos: if we have a poster image, show that. Otherwise, render the
  // actual <video> element with preload="metadata" so the browser reveals
  // the real first frame — never let SmartMedia fall back to "No media" or
  // a misleading stock image for videos that successfully uploaded.
  const showSrc = isVideo ? (poster || "") : (src || "");
  const showVideoFirstFrame = isVideo && !poster && !!src;
  const [computedFit, setComputedFit] = React.useState(fit === "auto" ? "cover" : fit);

  function handleLoad(e) {
    if (fit !== "auto") return;
    const img = e.currentTarget;
    if (!img || !img.naturalWidth || !img.naturalHeight) return;
    const ratio = img.naturalWidth / img.naturalHeight;
    // Tall (portrait) or near-square images get contain; landscape gets cover.
    setComputedFit(ratio < 1.05 ? "contain" : "cover");
  }

  return (
    <div
      onClick={onClick}
      className={`relative overflow-hidden border border-[#2c3122] ${rounded} ${bg} ${className}`}
      style={{ aspectRatio: aspect }}
    >
      {showSrc ? (
        <img
          src={showSrc}
          alt={alt}
          loading="lazy"
          onLoad={handleLoad}
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: computedFit, objectPosition: "center" }}
        />
      ) : showVideoFirstFrame ? (
        <video
          src={src}
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: "cover", objectPosition: "center", background: "#000" }}
        />
      ) : (
        <div className="absolute inset-0 grid place-items-center text-[#6e6f5f] text-[12px]">
          No media
        </div>
      )}

      {badge && (
        <div className="absolute top-2.5 left-2.5 px-2.5 py-1 rounded-md bg-black/75 text-[#c9a86b] text-[10px] tracking-[.2em] uppercase font-bold">
          {badge}
        </div>
      )}

      {isVideo && videoBadge && (
        <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded text-[10px] tracking-[.2em] uppercase bg-black/70 text-[#5fa15a] border border-[#5fa15a]/50">
          Video
        </span>
      )}

      {isVideo && showPlay && (
        <div className="absolute bottom-2.5 right-2.5 w-10 h-10 rounded-full bg-black/75 grid place-items-center text-white text-[14px] backdrop-blur">
          ▶
        </div>
      )}

      {children}
    </div>
  );
}

export function isVideoUrl(url) {
  if (!url) return false;
  const u = String(url).toLowerCase();
  return /\.(mp4|webm|mov|m4v|ogg|ogv)(\?|$)/.test(u) || /\/api\/media\/original\//.test(u);
}
